import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../layout/header';
import Footer from '../layout/footer';
import Modal from '../elements/modal';
import { getLocalItem, getSessionItem } from '../../utils/storage';
import { trackMetaEvent } from '../../utils/metaPixel';
import { getAcPricing } from '../../utils/vehicleApi';
import { getAcTiers, formatInr, getStartingPrice, PRICING } from '../../utils/pricing';

import {
  PremiumHero,
  StickyMobileCTA,
  SectionWrapper,
  PricingGrid,
  ComparisonTable,
  StepCards,
  WhyUsGrid,
  FaqAccordion,
  FinalCTA,
  Breadcrumbs,
  StructuredData,
} from '../elements/premium';

const CHECKLIST = [
  { icon: '❄️', title: 'AC Gas Top-Up (Add-on)', desc: 'Refrigerant refill (R134a / R1234yf) — charged separately based on quantity required.' },
  { icon: '🌀', title: 'Condenser Cleaning', desc: 'Deep clean of condenser fins to restore heat dissipation and cooling efficiency.' },
  { icon: '🌬️', title: 'AC Vent Cleaning', desc: 'Anti-bacterial clean of all cabin vents and ducts — eliminates odour.' },
  { icon: '🔍', title: 'Leak Detection Test', desc: 'Electronic pressure test to detect refrigerant leaks before they worsen.' },
  { icon: '🔧', title: 'Compressor Belt Check', desc: 'Inspect belt tension and condition; re-tension or flag for replacement.' },
  { icon: '🌡️', title: 'Cooling Performance Test', desc: 'Pre and post-service temperature reading to confirm optimal cooling.' },
  { icon: '💨', title: 'Cabin Air Filter Check', desc: 'Inspect cabin filter; clean or replace for fresh, allergen-free airflow.' },
  { icon: '⚡', title: 'Blower Motor Inspection', desc: 'Test all fan speeds and blower motor for correct airflow volume.' },
  { icon: '🎛️', title: 'AC Control Panel Check', desc: 'Verify that all control knobs, modes and temperature selectors function correctly.' },
  { icon: '🛡️', title: 'Compressor Clutch Test', desc: 'Verify clutch engagement under load — prevents catastrophic compressor failure.' },
  { icon: '🌿', title: 'Evaporator Inspection', desc: 'Visual check for signs of ice build-up, blockage or evaporator core damage.' },
  { icon: '📋', title: 'Post-Service Health Report', desc: 'Digital job card with findings, parts used, and next service recommendation.' },
];

const STEPS = [
  { no: '01', icon: '📱', title: 'Book Online or Call', desc: 'Select your vehicle type, choose a date & time slot. Takes under 2 minutes.' },
  { no: '02', icon: '🚐', title: 'Certified Mechanic Arrives', desc: 'Our uniformed, background-verified mechanic arrives at your doorstep on time.' },
  { no: '03', icon: '🔧', title: '12-Point AC Service', desc: 'Full AC diagnosis and service performed at your home, office, or parking spot.' },
  { no: '04', icon: '✅', title: 'Digital Report + Warranty', desc: 'You receive a job card, service photos, and a 30-day service warranty.' },
];

const WHY_US = [
  { icon: '🏠', title: 'Doorstep Service', desc: 'We come to you — no waiting at garages, no drop-offs, no hassle.' },
  { icon: '🎓', title: 'Certified Mechanics', desc: 'Background-verified technicians with brand-specific AC training and experience.' },
  { icon: '💰', title: 'Transparent Pricing', desc: 'Fixed price upfront. No hidden charges. No surprise bills after service.' },
  { icon: '🔒', title: '30-Day Warranty', desc: 'Every AC service carries a 30-day or 1,000km cooling performance guarantee.' },
  { icon: '📸', title: 'Before & After Photos', desc: 'Receive photo documentation of every job — full transparency, every time.' },
  { icon: '⚡', title: 'Same-Day Slots Available', desc: 'Book before noon and get your AC serviced the same day in most cities.' },
];

const AC_TIER_PRICE_SENTENCE = (getAcTiers() || []).map((t) => `${formatInr(t.price)} for ${t.label.toLowerCase()}`).join(', ') || '₹1,999 for hatchbacks';
// AC "from" price — single source of truth (snapshot), not a hardcoded literal.
const AC_FROM = getStartingPrice('ac').label || '₹1,999';

const FAQS = [
  { q: 'How much does Car AC Repair cost at Ride N Repair?', a: `Our Car AC service starts at ${AC_TIER_PRICE_SENTENCE}. This is a flat, all-inclusive price — gas refill, cleaning, and testing are all covered.` },
  { q: 'What is included in the Car AC service?', a: 'The service includes condenser cleaning, vent cleaning, cabin air filter check, leak detection test, compressor belt check, blower motor inspection, and a post-service cooling performance test. AC gas top-up is an add-on, billed separately based on the quantity needed.' },
  { q: 'Why is my car AC not cooling even after I switch it on?', a: 'The most common reasons are low refrigerant (gas leak or depletion), a blocked condenser, a failing compressor clutch, or a dirty cabin filter. Our 12-point service diagnoses and fixes all of these.' },
  { q: 'How often should I service my Car AC?', a: 'Ideally once a year before summer, or every 15,000 km — whichever comes first. Regular service prevents compressor failure, which is a much more expensive repair (₹15,000–₹35,000).' },
  { q: 'Do you service all car brands?', a: 'Yes. We service all major car brands including Maruti, Hyundai, Tata, Honda, Kia, MG, Toyota, Ford, Volkswagen, and luxury brands like Audi, BMW, Mercedes, and Jeep.' },
  { q: 'Is there a warranty on the Car AC service?', a: 'Yes. We provide a 30-day cooling performance warranty. If your AC performance drops within 30 days of service, we will revisit at no additional charge.' },
];

// Fallback data sourced from pricing-snapshot.json (backend SoT) when the
// live /v2/vehicle-data/ac-pricing endpoint is unavailable. Never hardcode.
const FALLBACK_PRICING = (getAcTiers() || []).map((t) => ({
  label: t.label,
  examples: t.examples || '',
  price: formatInr(t.price),
  rawPrice: t.price,
  vehicleName: t.label,
  highlight: !!t.highlight,
}));

// Comparison table — authorized/local-garage benchmarks vs RNR. Multipliers
// applied to snapshot RNR price are conservative public-market estimates
// (authorized ~2x, local ~1.5x). When backend exposes a real comparison
// table at /v2/vehicle-data/ac-pricing → comparison, prefer that.
const FALLBACK_COMPARISON = (getAcTiers() || []).map((t) => {
  const rnr = t.price;
  const auth = Math.round(rnr * 2);
  const local = Math.round(rnr * 1.55);
  return { type: t.label, authorized: formatInr(auth), local: formatInr(local), rnr: formatInr(rnr), save: 'Save ' + formatInr(auth - rnr) };
});

class CarAcRepairPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pricing: FALLBACK_PRICING,
      comparison: FALLBACK_COMPARISON,
    };
  }

  async componentDidMount() {
    const { setOpenModal, isMobileApp, history } = this.props;
    if (
      getLocalItem('phone_provided') === 'yes' ||
      getSessionItem('phone_ignored') === 'yes' ||
      this.props.user.is_logged
    ) {
      setOpenModal(false);
    }
    if (isMobileApp) {
      history.push('/register');
    }
    trackMetaEvent('AcRepairPageView');

    // Fetch pricing from API
    try {
      const data = await getAcPricing();
      if (data.pricing && data.pricing.length > 0) {
        const pricing = data.pricing.map((p) => ({
          label: p.label,
          examples: p.examples,
          price: `₹${p.price.toLocaleString('en-IN')}`,
          rawPrice: p.price,
          vehicleName: p.vehicle_name || p.label,
          highlight: p.highlight || false,
        }));
        this.setState({ pricing });
      }
      if (data.comparison && data.comparison.length > 0) {
        const comparison = data.comparison.map((c) => ({
          type: c.type,
          authorized: c.authorized,
          local: c.local,
          rnr: c.rnr,
          save: c.save,
        }));
        this.setState({ comparison });
      }
    } catch (err) {
      // Use fallback pricing
    }
  }

  render() {
    const {
      isMobileBrowser,
      isMobileApp,
      user,
      setUser,
      setCitiesModal,
      openModal,
      setOpenModal,
    } = this.props;
    const { pricing, comparison } = this.state;
    const canonical = 'https://www.ridenrepair.com/car-ac-repair-near-me';

    const schemas = [
      {
        '@type': 'Service',
        name: 'Car AC Repair',
        provider: { '@type': 'Organization', name: 'Ride N Repair' },
        description: `Doorstep Car AC repair and service starting at ${AC_FROM}.`,
        offers: (pricing || []).map((p) => ({
          '@type': 'Offer',
          name: `${p.label} AC Service`,
          price: String(p.rawPrice),
          priceCurrency: 'INR',
        })),
        areaServed: { '@type': 'Country', name: 'India' },
      },
    ];

    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <title>{`Car AC Repair Near Me | Starting ${AC_FROM} – Ride N Repair`}</title>
          <meta name="description" content={`Car AC Repair at ${AC_FROM} – doorstep service, certified mechanics, 30-day warranty. AC gas refill, condenser cleaning, leak test & more. Book now!`} />
          <meta name="keywords" content="car ac repair near me, car ac service, car ac repair, ac gas refill, car ac not cooling, ac repair doorstep" />
          <link rel="canonical" href={canonical} />
          <meta property="og:title" content={`Car AC Repair Near Me | ${AC_FROM} – Ride N Repair`} />
          <meta property="og:description" content={`Doorstep Car AC Repair starting ${AC_FROM}. Certified mechanics, 12-point service, 30-day warranty.`} />
          <meta property="og:url" content={canonical} />
          <meta property="og:type" content="website" />
        </Helmet>

        <StructuredData data={schemas} />

        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} />

        {!isMobileApp && openModal && (
          <Modal isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} openModal={openModal} setOpenModal={setOpenModal} user={user} setUser={setUser} />
        )}

        {/* HERO */}
        <PremiumHero
          bgImage="/ac-repair/hero-bg.webp"
          bgAlt="Car AC Repair Service"
          badge="Doorstep Service"
          title="Car AC Repair"
          priceTag={`Starting ${AC_FROM}`}
          subtitle="Certified mechanics at your doorstep. 12-point AC service with 30-day cooling warranty. Gas refill available as add-on."
          isMobileBrowser={isMobileBrowser}
          showCarAnimation
          ctas={[
            { label: `Book Now — ${AC_FROM} Onwards`, href: '#ac-pricing', variant: 'primary' },
            { label: 'Call: +91 120 361 5050', href: 'tel:+911203615050', variant: 'secondary' },
          ]}
          trustItems={[
            { value: '2,00,000+', label: 'Customers Served' },
            { value: '4.8★', label: 'Customer Rating' },
            { value: '30-Day', label: 'Cooling Warranty' },
            { value: 'Same-Day', label: 'Slots Available' },
          ]}
        />

        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Car AC Repair' }]} />

        {/* STICKY MOBILE CTA */}
        <StickyMobileCTA title="Car AC Repair" priceHint={`From ${AC_FROM} · 30-Day Warranty`} btnLabel="Book Now" btnHref="#ac-pricing" />

        {/* WHAT'S INCLUDED — 12-point checklist */}
        <SectionWrapper kicker="12-Point Service" heading="What's Covered in Your Car AC Service" subtitle="Every booking includes a comprehensive 12-point inspection and service — no add-on surprises.">
          <div className="rr-card-grid">
            {CHECKLIST.map((item) => (
              <div key={item.title} className="rr-check-card">
                <div className="rr-check-card__icon">{item.icon}</div>
                <div>
                  <div className="rr-check-card__title">{item.title}</div>
                  <div className="rr-check-card__desc">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="rr-img-wrap">
            <img src="/ac-repair/technician.webp" alt="Ride N Repair technician servicing car AC system" className="rr-img-wrap__img" loading="lazy" />
            <div className="rr-img-wrap__caption">Our certified mechanic at your doorstep — no garage visit needed.</div>
          </div>
        </SectionWrapper>

        {/* PRICING PACKAGES */}
        <SectionWrapper id="ac-pricing" variant="dark" kicker="Transparent Pricing" heading="Car AC Repair — Pick Your Vehicle" subtitle="All prices include gas refill, cleaning, leak test and service warranty. What you see is what you pay.">
          <PricingGrid
            items={(pricing || []).map((pkg) => ({
              label: pkg.label,
              examples: pkg.examples,
              price: pkg.price,
              priceSub: 'Service charge · Gas refill billed separately',
              highlight: pkg.highlight,
              badge: pkg.highlight ? 'Most Popular' : undefined,
              features: [
                'Condenser & Vent Cleaning',
                'Leak Detection Test',
                'Blower & Compressor Check',
                'Cooling Performance Test',
                'Post-service Health Report',
                '30-Day Warranty',
                '+ Gas Refill if needed (add-on)',
              ],
              btnLabel: 'Book Now',
              btnComponent: (
                <Link
                  to={{
                    pathname: '/checkout',
                    state: {
                      vehicle_category: 'car',
                      vehicle_name: pkg.vehicleName,
                      service_type: 'AC Repair',
                      price: pkg.rawPrice,
                      brand: '',
                      fuel_type: '',
                      transmission: '',
                      cc_range: '',
                    },
                  }}
                  className={pkg.highlight ? 'rr-pricing-card__btn rr-pricing-card__btn--hl' : 'rr-pricing-card__btn'}
                >
                  Book Now
                </Link>
              ),
            }))}
          />
        </SectionWrapper>

        {/* PRICE COMPARISON */}
        <SectionWrapper kicker="Save More" heading="Price Comparison — Why Ride N Repair Wins" subtitle="Get dealership-quality AC repair at a fraction of the cost.">
          <ComparisonTable
            columns={[
              { label: 'Vehicle Type' },
              { label: 'Authorized Center', icon: '🏢' },
              { label: 'Local Mechanic', icon: '🔧' },
              { label: 'Ride N Repair', icon: '⭐', isRnr: true },
            ]}
            rows={(comparison || []).map((row) => ({
              cells: [
                { text: row.type, bold: true },
                { text: row.authorized, strikethrough: true },
                { text: row.local, faded: true },
                { text: row.rnr, saveBadge: row.save, isRnr: true },
              ],
            }))}
            note="* Prices compared vs. authorized service centers. Savings vary by city and vehicle model. Ride N Repair pricing includes gas refill, cleaning, and warranty."
            ctaLabel="Book at Best Price"
            ctaHref="#ac-pricing"
          />
        </SectionWrapper>

        {/* HOW IT WORKS */}
        <SectionWrapper variant="dark" kicker="Simple Process" heading="How Car AC Repair Works">
          <StepCards steps={STEPS} variant="dark" />
        </SectionWrapper>

        {/* WHY RIDE N REPAIR */}
        <SectionWrapper variant="gray" kicker="Our Promise" heading="Why 2,00,000+ Customers Choose Ride N Repair">
          <WhyUsGrid items={WHY_US} />
        </SectionWrapper>

        {/* FAQ */}
        <SectionWrapper kicker="Common Questions" heading="Car AC Repair — FAQ">
          <FaqAccordion faqs={FAQS} />
        </SectionWrapper>

        {/* FINAL CTA */}
        <FinalCTA
          heading={<>Is Your Car AC Not Cooling?<br />Get It Fixed Today.</>}
          subtitle={`Doorstep service · Certified mechanics · ${AC_FROM} onwards · 30-day warranty`}
          ctas={[
            { label: 'Book Now', href: '#ac-pricing', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
          ]}
        />

        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
      </div>
    );
  }
}

export default CarAcRepairPage;
